/** Pure caption burn-in text composition (unit tested). See ARCHITECTURE.md §6a. */

import { MAX_CAPTION_LENGTH } from "./validation";

/**
 * Extract the display name for attribution from the guest's entered name.
 * "Sarah Connor"      -> "Sarah"
 * "Sarah & Tom Smith" -> "Sarah & Tom"
 * "Sarah and Tom"     -> "Sarah & Tom"
 * "sarah"             -> "Sarah" (capitalized)
 */
export function attributionName(fullName: string): string {
  const cleaned = fullName.replace(/\s+/g, " ").trim();
  if (!cleaned) return "";
  const couple = cleaned.match(/^(\S+)\s+(?:&|and|\+)\s+(\S+)/i);
  if (couple) {
    return `${capitalize(couple[1]!)} & ${capitalize(couple[2]!)}`;
  }
  const first = cleaned.split(" ")[0]!;
  return capitalize(first);
}

function capitalize(word: string): string {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** "July 2026" — month/year in the guest's locale (locale injectable for tests). */
export function monthYear(date: Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date);
}

/** '— Sarah, July 2026' attribution line. Empty string when no usable name. */
export function composeAttribution(fullName: string, date: Date, locale?: string): string {
  const name = attributionName(fullName);
  if (!name) return "";
  return `— ${name}, ${monthYear(date, locale)}`;
}

/** Caption line as burned in: trimmed, whitespace collapsed, quoted, length capped. */
export function composeCaptionLine(caption: string): string {
  const cleaned = caption.replace(/\s+/g, " ").trim().slice(0, MAX_CAPTION_LENGTH);
  if (!cleaned) return "";
  return `“${cleaned}”`;
}

export interface StripText {
  captionLine: string;
  attribution: string;
}

/**
 * Full strip text, e.g. { captionLine: '“Best weekend ever!”', attribution: '— Sarah, July 2026' }.
 * Returns null when nothing should be burned in (no caption -> no empty strip, per architecture).
 */
export function composeStripText(
  caption: string,
  fullName: string,
  date: Date,
  locale?: string
): StripText | null {
  const captionLine = composeCaptionLine(caption);
  if (!captionLine) return null;
  return { captionLine, attribution: composeAttribution(fullName, date, locale) };
}

/** Burn-in happens only when the property enables it AND the guest wrote a caption. */
export function shouldBurnIn(captionBurnInEnabled: boolean, caption: string): boolean {
  return captionBurnInEnabled && composeCaptionLine(caption) !== "";
}
